import { useState } from 'react';
import { Calendar as CalendarIcon, X, Plus } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

// Slot format stored as ISO strings: "2026-06-20T10:00|2026-06-21T17:00"
// Displayed as: "20 Jun 2026 10:00 AM → 21 Jun 2026 5:00 PM"

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = ['00', '15', '30', '45'];

const formatSlot = (slotStr) => {
  if (!slotStr) return '';
  const [from, to] = slotStr.split('|');
  const fromDate = parseISO(from);
  const toDate = parseISO(to);
  if (!isValid(fromDate) || !isValid(toDate)) return slotStr;
  return `${format(fromDate, 'd MMM yyyy, h:mm a')} → ${format(toDate, 'd MMM yyyy, h:mm a')}`;
};

const DateTimePicker = ({ label, value, onChange, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const currentDate = value ? parseISO(value) : null;
  const currentHour = currentDate ? currentDate.getHours() : 10;
  const currentMinute = currentDate ? String(currentDate.getMinutes()).padStart(2, '0') : '00';

  const updateDatePart = (date) => {
    if (!date) return;
    const d = new Date(date);
    d.setHours(currentHour, parseInt(currentMinute), 0, 0);
    onChange(formatISO(d));
  };

  const updateHour = (h) => {
    const base = currentDate || new Date();
    const d = new Date(base);
    d.setHours(h, parseInt(currentMinute), 0, 0);
    onChange(formatISO(d));
  };

  const updateMinute = (m) => {
    const base = currentDate || new Date();
    const d = new Date(base);
    d.setHours(currentHour, parseInt(m), 0, 0);
    onChange(formatISO(d));
  };

  return (
    <div className="flex-1">
      <label
        className="block mb-1 uppercase"
        style={{ fontFamily: 'Manrope', fontSize: '11px', color: '#5C5C5C', letterSpacing: '0.15em' }}
      >
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-3 py-2 border border-[#E5E0D8] rounded-md text-left"
            style={{ fontFamily: 'Manrope', fontSize: '14px', backgroundColor: '#F9F6F0', color: '#1A1A1A' }}
            data-testid={`${testIdPrefix}-trigger`}
          >
            <CalendarIcon size={16} strokeWidth={1.5} style={{ color: '#5C5C5C' }} />
            {currentDate ? format(currentDate, 'd MMM yyyy, h:mm a') : 'Pick date & time'}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid={`${testIdPrefix}-popover`}>
          <div className="flex flex-col md:flex-row">
            <Calendar
              mode="single"
              selected={currentDate || undefined}
              onSelect={updateDatePart}
              initialFocus
            />
            <div className="border-t md:border-t-0 md:border-l border-[#E5E0D8] p-3 flex gap-2 md:flex-col md:justify-center">
              <div>
                <div
                  className="mb-1 uppercase"
                  style={{ fontFamily: 'Manrope', fontSize: '10px', color: '#5C5C5C', letterSpacing: '0.15em' }}
                >
                  Hour
                </div>
                <select
                  value={currentHour}
                  onChange={(e) => updateHour(parseInt(e.target.value))}
                  className="px-2 py-1 border border-[#E5E0D8] rounded"
                  style={{ fontFamily: 'Manrope', fontSize: '13px', backgroundColor: '#F9F6F0' }}
                  data-testid={`${testIdPrefix}-hour`}
                >
                  {HOURS.map((h) => {
                    const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
                    return (
                      <option key={h} value={h}>
                        {`${h.toString().padStart(2, '0')} (${label})`}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <div
                  className="mb-1 uppercase"
                  style={{ fontFamily: 'Manrope', fontSize: '10px', color: '#5C5C5C', letterSpacing: '0.15em' }}
                >
                  Minute
                </div>
                <select
                  value={currentMinute}
                  onChange={(e) => updateMinute(e.target.value)}
                  className="px-2 py-1 border border-[#E5E0D8] rounded"
                  style={{ fontFamily: 'Manrope', fontSize: '13px', backgroundColor: '#F9F6F0' }}
                  data-testid={`${testIdPrefix}-minute`}
                >
                  {MINUTES.map((m) => (
                    <option key={m} value={m}>:{m}</option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md md:mt-2"
                style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                data-testid={`${testIdPrefix}-done`}
              >
                Done
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Convert Date -> ISO string without seconds/ms (for stable storage)
const formatISO = (d) => {
  const yyyy = d.getFullYear();
  const MM = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const HH = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${MM}-${dd}T${HH}:${mm}`;
};

export const AvailabilitySlotsPicker = ({ value = [], onChange }) => {
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [error, setError] = useState('');

  const addSlot = () => {
    setError('');
    if (!draftFrom || !draftTo) {
      setError('Please pick both a "From" and "To" date/time');
      return;
    }
    if (new Date(draftFrom) >= new Date(draftTo)) {
      setError('"To" must be after "From"');
      return;
    }
    const slot = `${draftFrom}|${draftTo}`;
    if (value.includes(slot)) {
      setError('That slot is already added');
      return;
    }
    onChange([...value, slot]);
    setDraftFrom('');
    setDraftTo('');
  };

  const removeSlot = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid="availability-slots-picker">
      <label className="block mb-2" style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500 }}>
        Available Dates & Times
      </label>
      <p className="mb-3" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>
        Add one or more date-and-time windows when buyers can pick up or receive delivery.
      </p>

      <div className="p-4 rounded-md border border-[#E5E0D8] mb-3" style={{ backgroundColor: '#F9F6F0' }}>
        <div className="flex flex-col md:flex-row gap-3 mb-3">
          <DateTimePicker
            label="From"
            value={draftFrom}
            onChange={setDraftFrom}
            testIdPrefix="slot-from"
          />
          <DateTimePicker
            label="To"
            value={draftTo}
            onChange={setDraftTo}
            testIdPrefix="slot-to"
          />
        </div>
        {error && (
          <p className="mb-3" style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#991B1B' }} data-testid="slot-error">
            {error}
          </p>
        )}
        <Button
          type="button"
          onClick={addSlot}
          className="rounded-md"
          style={{ backgroundColor: '#2E5C41', color: '#FFFFFF', fontFamily: 'Manrope', fontWeight: 600 }}
          data-testid="add-slot-btn"
        >
          <Plus size={16} className="mr-1" />
          Add Time Slot
        </Button>
      </div>

      {value.length > 0 && (
        <div className="space-y-2" data-testid="slots-list">
          {value.map((slot, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-3 py-2 rounded-md border border-[#E5E0D8]"
              style={{ backgroundColor: '#FFFFFF' }}
              data-testid={`slot-${idx}`}
            >
              <div className="flex items-center gap-2">
                <CalendarIcon size={16} strokeWidth={1.5} style={{ color: '#2E5C41' }} />
                <span style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#1A1A1A' }}>
                  {formatSlot(slot)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeSlot(idx)}
                className="p-1 rounded hover:bg-[#F9F6F0]"
                style={{ transition: 'background-color 0.2s' }}
                data-testid={`remove-slot-${idx}`}
                aria-label="Remove slot"
              >
                <X size={16} strokeWidth={1.5} style={{ color: '#991B1B' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper for read-only display anywhere in the app
export const formatAvailabilitySlot = formatSlot;
